import React, { useState, useEffect } from 'react';
import { Filter, X, Calendar, Search, Phone, Mail, User } from 'lucide-react';
import { cn } from '@/utils/cn';
import FSelect from '@/components/ui/FSelect';

interface FilterOption {
    opt_id: string | number;
    opt_name: string;
    uuid?: string;
}

interface ColumnConfig {
    col_id: string;
    col_name: string;
    col_label: string;
    col_type: string;
    col_required?: boolean;
    col_order?: number;
    options?: FilterOption[];
}

interface PipelineFiltersEnhancedProps {
    isOpen: boolean;
    onClose: () => void;
    columns: ColumnConfig[];
    onApplyFilters: (filters: any) => void;
    currentFilters?: any;
    isLoading?: boolean;
    // When set, Additional Filters shows only these columns, in this order (Vista Form clients).
    additionalFilterOrder?: string[];
    // Extra From/To date filters shown under Date Filters, e.g. Next Follow-up Date.
    extraDateFilters?: { key: string; label: string }[];
    // Labels for the Phone / Name / Email search boxes (Vista Form clients use their own names).
    textFilterLabels?: Partial<Record<'phone' | 'name' | 'email', string>>;
}

type DateRange = { startDate?: string; endDate?: string };

const PipelineFiltersEnhanced: React.FC<PipelineFiltersEnhancedProps> = ({
    isOpen,
    onClose,
    columns,
    onApplyFilters,
    currentFilters = {},
    isLoading = false,
    additionalFilterOrder,
    extraDateFilters = [],
    textFilterLabels = {}
}) => {
    const dateKeys = ['created_date', 'updated_date', ...extraDateFilters.map((d) => d.key)];
    const emptyDates = () => Object.fromEntries(dateKeys.map((k) => [k, {}])) as Record<string, DateRange>;
    const [filters, setFilters] = useState<Record<string, any>>({});
    const [textFilters, setTextFilters] = useState({
        name: '',
        phone: '',
        email: ''
    });
    const [dateFilters, setDateFilters] = useState<Record<string, DateRange>>(emptyDates);

    useEffect(() => {
        if (isOpen && currentFilters) {
            // Load existing filters
            const { name, phone, email, ...rest } = currentFilters;
            setTextFilters({
                name: name || '',
                phone: phone || '',
                email: email || ''
            });
            const loadedDates = emptyDates();
            const selectFilters: Record<string, any> = {};
            Object.entries(rest).forEach(([k, v]) => {
                if (dateKeys.includes(k)) loadedDates[k] = (v as DateRange) || {};
                else selectFilters[k] = v;
            });
            setDateFilters(loadedDates);
            setFilters(selectFilters);
        }
    }, [isOpen, currentFilters]);

    const handleSelectFilter = (colName: string, values: any[]) => {
        setFilters(prev => ({
            ...prev,
            [colName]: values.length > 0 ? values : undefined
        }));
    };

    const handleTextFilter = (field: 'name' | 'phone' | 'email', value: string) => {
        setTextFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleDateFilter = (type: string, field: 'startDate' | 'endDate', value: string) => {
        setDateFilters(prev => ({
            ...prev,
            [type]: {
                ...prev[type],
                [field]: value
            }
        }));
    };


    const handleApply = () => {
        const allFilters: any = {
            ...filters
        };

        // Add text filters if not empty
        if (textFilters.name?.trim()) allFilters.name = textFilters.name.trim();
        if (textFilters.phone?.trim()) allFilters.phone = textFilters.phone.trim();
        if (textFilters.email?.trim()) allFilters.email = textFilters.email.trim();

        // Add date filters if not empty
        dateKeys.forEach((k) => {
            if (dateFilters[k]?.startDate || dateFilters[k]?.endDate) allFilters[k] = dateFilters[k];
        });

        // Remove undefined values
        Object.keys(allFilters).forEach(key => {
            if (allFilters[key] === undefined || allFilters[key] === null ||
                (Array.isArray(allFilters[key]) && allFilters[key].length === 0)) {
                delete allFilters[key];
            }
        });

        console.log('Applying filters:', allFilters);
        onApplyFilters(allFilters);
        onClose();
    };

    const handleClear = () => {
        setFilters({});
        setTextFilters({ name: '', phone: '', email: '' });
        setDateFilters(emptyDates());

        // Auto-apply empty filters (clear all)
        onApplyFilters({});
        onClose();
    };

    const getActiveFilterCount = () => {
        let count = 0;

        // Count select filters
        Object.keys(filters).forEach(key => {
            if (filters[key] && Array.isArray(filters[key]) && filters[key].length > 0) {
                count += filters[key].length;
            }
        });

        // Count text filters
        if (textFilters.name) count++;
        if (textFilters.phone) count++;
        if (textFilters.email) count++;

        // Count date filters
        dateKeys.forEach((k) => { if (dateFilters[k]?.startDate || dateFilters[k]?.endDate) count++; });

        return count;
    };

    if (!isOpen) return null;

    // Sort columns by order
    const sortedColumns = [...columns].sort((a, b) => (a.col_order || 999) - (b.col_order || 999));
    const isSelectFilter = (col: ColumnConfig) => col.col_type === 'select' && !!col.options && col.options.length > 0;
    const additionalFilterColumns = additionalFilterOrder
        ? additionalFilterOrder.map((name) => columns.find((c) => c.col_name === name)).filter((c): c is ColumnConfig => !!c && isSelectFilter(c))
        : sortedColumns.filter(isSelectFilter);

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black bg-opacity-50 z-40"
                onClick={onClose}
            />

            {/* Sidebar - Increased width */}
            <div className="fixed right-0 top-0 h-full w-[600px] bg-white shadow-xl z-50 flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-3">
                        <Filter className="h-5 w-5 text-gray-600" />
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Advanced Filters</h2>
                            <p className="text-xs text-gray-500 mt-0.5">Filter leads by multiple criteria</p>
                        </div>
                        {getActiveFilterCount() > 0 && (
                            <span className="px-2.5 py-1 bg-brandColor text-white text-xs rounded-full font-semibold">
                                {getActiveFilterCount()} active
                            </span>
                        )}
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-5">
                    {/* Two Column Grid Layout */}
                    <div className="grid grid-cols-2 gap-x-6 gap-y-4">
                        {/* Phone Field - Top Priority */}
                        <div className="col-span-2">
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
                                <Phone className="h-3.5 w-3.5" />
                                {textFilterLabels.phone || 'Phone'}
                            </label>
                            <input
                                type="text"
                                value={textFilters.phone}
                                onChange={(e) => handleTextFilter('phone', e.target.value)}
                                placeholder="Search by phone number..."
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                            />
                        </div>

                        {/* Date Filters Section */}
                        <div className="col-span-2 mt-4">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-gray-500" />
                                Date Filters
                            </h3>
                        </div>

                        {/* Created Date From */}
                        <div>
                            <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                                Created Date From
                            </label>
                            <input
                                type="date"
                                value={dateFilters.created_date?.startDate || ''}
                                onChange={(e) => handleDateFilter('created_date', 'startDate', e.target.value)}
                                className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor"
                            />
                        </div>

                        {/* Created Date To */}
                        <div>
                            <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                                Created Date To
                            </label>
                            <input
                                type="date"
                                value={dateFilters.created_date?.endDate || ''}
                                onChange={(e) => handleDateFilter('created_date', 'endDate', e.target.value)}
                                className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor"
                            />
                        </div>

                        {/* Updated Date From */}
                        <div>
                            <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                                Updated Date From
                            </label>
                            <input
                                type="date"
                                value={dateFilters.updated_date?.startDate || ''}
                                onChange={(e) => handleDateFilter('updated_date', 'startDate', e.target.value)}
                                className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor"
                            />
                        </div>

                        {/* Updated Date To */}
                        <div>
                            <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                                Updated Date To
                            </label>
                            <input
                                type="date"
                                value={dateFilters.updated_date?.endDate || ''}
                                onChange={(e) => handleDateFilter('updated_date', 'endDate', e.target.value)}
                                className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor"
                            />
                        </div>

                        {extraDateFilters.map(({ key, label }) => (
                            <React.Fragment key={key}>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 mb-1.5 block">{label} From</label>
                                    <input
                                        type="date"
                                        value={dateFilters[key]?.startDate || ''}
                                        onChange={(e) => handleDateFilter(key, 'startDate', e.target.value)}
                                        className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-700 mb-1.5 block">{label} To</label>
                                    <input
                                        type="date"
                                        value={dateFilters[key]?.endDate || ''}
                                        onChange={(e) => handleDateFilter(key, 'endDate', e.target.value)}
                                        className="w-full text-sm border border-gray-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brandColor"
                                    />
                                </div>
                            </React.Fragment>
                        ))}

                        {/* Primary Fields Section */}
                        <div className="col-span-2 mt-4">
                            <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
                                <Search className="h-4 w-4 text-gray-500" />
                                Primary Fields
                            </h3>
                        </div>

                        {/* Name Field */}
                        <div>
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
                                <User className="h-3.5 w-3.5" />
                                {textFilterLabels.name || 'Name'}
                            </label>
                            <input
                                type="text"
                                value={textFilters.name}
                                onChange={(e) => handleTextFilter('name', e.target.value)}
                                placeholder="Search by name..."
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                            />
                        </div>

                        {/* Email Field */}
                        <div>
                            <label className="flex items-center gap-2 text-xs font-medium text-gray-700 mb-1.5">
                                <Mail className="h-3.5 w-3.5" />
                                {textFilterLabels.email || 'Email'}
                            </label>
                            <input
                                type="email"
                                value={textFilters.email}
                                onChange={(e) => handleTextFilter('email', e.target.value)}
                                placeholder="Search by email address..."
                                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brandColor focus:border-transparent"
                            />
                        </div>

                        {/* Dynamic Column Filters */}
                        {additionalFilterColumns.length > 0 && (
                            <div className="col-span-2 mt-4">
                                <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                                    Additional Filters
                                </h3>
                            </div>
                        )}

                        {additionalFilterColumns.map((column) => (
                            <div key={column.col_id}>
                                <label className="text-xs font-medium text-gray-700 mb-1.5 block">
                                    {column.col_label}
                                    {column.col_required && <span className="text-red-500 ml-1">*</span>}
                                    {filters[column.col_name] && filters[column.col_name].length > 0 && (
                                        <span className="ml-2 px-2 py-0.5 bg-brandColor text-white text-xs rounded-full">
                                            {filters[column.col_name].length}
                                        </span>
                                    )}
                                </label>
                                <FSelect
                                    options={column.options || []}
                                    value={filters[column.col_name] || []}
                                    onChange={(values) => handleSelectFilter(column.col_name, values)}
                                    placeholder={`Select ${column.col_label}...`}
                                    searchable={true}
                                    showCount={false}
                                    className="w-full"
                                    wideDropdown={true}
                                />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-5 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center justify-between mb-3">
                        <span className="text-sm text-gray-600">
                            {getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} selected
                        </span>
                        {getActiveFilterCount() > 0 && (
                            <button
                                onClick={handleClear}
                                className="text-sm text-red-600 hover:text-red-700 font-medium"
                            >
                                Clear All
                            </button>
                        )}
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={onClose}
                            className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleApply}
                            disabled={isLoading}
                            className={cn(
                                "flex-1 px-4 py-2.5 text-sm font-medium rounded-lg transition-colors",
                                isLoading
                                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                    : "bg-brandColor text-white hover:bg-brand-hover shadow-sm"
                            )}
                        >
                            {isLoading ? 'Applying...' : 'Apply Filters'}
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default PipelineFiltersEnhanced;